import React, { ChangeEvent, FocusEvent, useContext } from 'react';

import { observer } from 'mobx-react-lite';

import Button from 'components/common/Button/Button';
import TextInput from 'components/common/TextInput/TextInput';
import MultiLineInput from 'components/common/MultiLineInput/MultiLineInput';
import { isValidPhone, validateFirstName, validateRequiredField } from 'common/utils/validation.utils';
import { validateEmail } from 'components/auth/authValidation.utils';
import PhoneNumberInput from 'components/common/PhoneNumberInput/PhoneNumberInput';
import { RootStoreContext } from 'index';
import useForm, { FieldValues } from 'hooks/useForm';

import { ContactHfsLabels } from './contactHfsDialog.enums';
import styles from './ContactHfsDialog.module.scss';
import { ValidateContactHfsFormResult } from './contactHfsDialog.types';

interface ContactHfsPropsType {
  onSubmit: (values: ValidateContactHfsFormResult) => void;
}

const ContactHfs = ({ onSubmit }: ContactHfsPropsType) => {
  const { intermediaryStore } = useContext(RootStoreContext);

  const { errors, values, setValue, validateField, handleSubmit } = useForm<FieldValues>({
    defaultValues: {
      [ContactHfsLabels.FirstName]: '',
      [ContactHfsLabels.LastName]: '',
      [ContactHfsLabels.Email]: '',
      [ContactHfsLabels.Phone]: '',
      [ContactHfsLabels.Message]: '',
    },
    handleValidation: (fieldName, value) => {
      switch (fieldName) {
        case ContactHfsLabels.FirstName: {
          return validateRequiredField(value, fieldName) || validateFirstName(value, fieldName);
        }
        case ContactHfsLabels.LastName: {
          return validateRequiredField(value, fieldName);
        }
        case ContactHfsLabels.Email: {
          return validateEmail(value, fieldName);
        }
        case ContactHfsLabels.Phone: {
          return isValidPhone(value);
        }
        case ContactHfsLabels.Message: {
          return validateRequiredField(value, fieldName);
        }
      }

      return '';
    },
  });

  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;

    setValue(name, value);
  };

  const handlePhoneChange = (phone: string) => {
    setValue(ContactHfsLabels.Phone, phone);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name } = event.target;

    validateField(name, values[name]);
  };

  const handleMessageSubmit = handleSubmit(async () => {
    onSubmit({
      email: values[ContactHfsLabels.Email],
      phone: values[ContactHfsLabels.Phone],
      firstName: values[ContactHfsLabels.FirstName],
      lastName: values[ContactHfsLabels.LastName],
      message: values[ContactHfsLabels.Message],
    });
  });

  return (
    <div className={styles.inputContainer}>
      <TextInput
        className={styles.input}
        containerClassName={styles.inputContainer}
        labelTitle={ContactHfsLabels.FirstName}
        name={ContactHfsLabels.FirstName}
        tabIndex={1}
        errorMessage={errors[ContactHfsLabels.FirstName]}
        onChange={handleChange}
        onBlur={handleBlur}
      />
      <TextInput
        className={styles.input}
        containerClassName={styles.inputContainer}
        labelTitle={ContactHfsLabels.LastName}
        name={ContactHfsLabels.LastName}
        tabIndex={1}
        errorMessage={errors[ContactHfsLabels.LastName]}
        onChange={handleChange}
        onBlur={handleBlur}
      />
      <PhoneNumberInput
        className={styles.input}
        containerClassName={styles.inputContainer}
        labelTitle={ContactHfsLabels.Phone}
        name={ContactHfsLabels.Phone}
        tabIndex={1}
        errorMessage={errors[ContactHfsLabels.Phone]}
        onChange={handlePhoneChange as any}
        onBlur={handleBlur}
      />
      <TextInput
        className={styles.input}
        containerClassName={styles.inputContainer}
        labelTitle={ContactHfsLabels.Email}
        name={ContactHfsLabels.Email}
        tabIndex={1}
        errorMessage={errors[ContactHfsLabels.Email]}
        onChange={handleChange}
        onBlur={handleBlur}
      />
      <MultiLineInput
        containerClassName={styles.inputContainer}
        labelTitle={ContactHfsLabels.Message}
        name={ContactHfsLabels.Message}
        tabIndex={1}
        errorMessage={errors[ContactHfsLabels.Message]}
        onChange={handleChange}
        onBlur={handleBlur}
      />
      <Button className={styles.button} onClick={handleMessageSubmit} isLoading={intermediaryStore.isLoading}>
        Send Message
      </Button>
    </div>
  );
};

export default observer(ContactHfs);
